/**
 * TournamentRegistrationPage.js — Public tournament registration page.
 * Route: /join/t/:joinSlug (public, no auth required, mobile-optimized)
 *
 * Detects registration method:
 *   - URL param ?method=qr → qr_scan
 *   - Default → direct_link
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Logo from './Logo';
import { getTournamentJoinInfo, submitTournamentRegistration } from '../services/api';
import './TournamentRegistrationPage.css';

const SPORT_LABELS = {
  general: 'General',
  soccer: 'Soccer',
  basketball: 'Basketball',
  volleyball: 'Volleyball',
  softball: 'Softball',
  hockey: 'Hockey',
  football: 'Football',
  winloss: 'Win/Loss',
  pool: 'Pool',
  darts: 'Darts',
  pickleball: 'Pickleball'
};

const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });
}

function TournamentRegistrationPage() {
  const { joinSlug } = useParams();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const [form, setForm] = useState({
    playerName: '',
    email: '',
    phone: '',
    teamName: '',
    isFreeAgent: false,
    skillLevel: '',
    waiverAccepted: false
  });

  const registrationMethod = searchParams.get('method') === 'qr' ? 'qr_scan' : 'direct_link';

  useEffect(() => {
    getTournamentJoinInfo(joinSlug)
      .then(data => {
        setInfo(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [joinSlug]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const result = await submitTournamentRegistration(joinSlug, {
        ...form,
        registrationMethod
      });
      setConfirmation(result);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="trp-page">
        <div className="trp-loading">Loading tournament info...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trp-page">
        <div className="trp-card">
          <div className="trp-logo"><Logo /></div>
          <div className="trp-error-banner">{error}</div>
        </div>
      </div>
    );
  }

  const { tournament, registrationOpen, isFull, teamCount, spotsRemaining, deadlinePassed } = info;

  if (submitted && confirmation) {
    const teamLabel = confirmation.team
      ? `Team "${confirmation.team.teamName}" has been registered.`
      : 'You have been added to the free agent pool.';
    return (
      <div className="trp-page">
        <div className="trp-card">
          <div className="trp-logo"><Logo /></div>
          <div className="trp-success">
            <div className="trp-success-icon">✓</div>
            <h1 className="trp-success-title">You're registered!</h1>
            <p className="trp-success-name">Hi {confirmation.registration.playerName}</p>
            <p className="trp-success-detail">{teamLabel}</p>
            <div className="trp-success-tournament">
              <strong>{tournament.name}</strong>
              {tournament.startDate && <span>{formatDate(tournament.startDate)}</span>}
            </div>
            {confirmation.registration.email && (
              <p className="trp-success-email">
                A confirmation email has been sent to <strong>{confirmation.registration.email}</strong>.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const showSkillLevel = tournament.settings?.skillLevelEnabled;
  const showWaiver = tournament.settings?.waiverEnabled;
  const entryFee = tournament.entryFee ? `$${parseFloat(tournament.entryFee).toFixed(2)}` : null;
  const sportLabel = SPORT_LABELS[tournament.sport] || tournament.sport || 'Tournament';

  return (
    <div className="trp-page">
      <div className="trp-card">
        <div className="trp-logo"><Logo /></div>

        {/* Tournament Header */}
        <div className="trp-header">
          <div className="trp-sport-badge">{sportLabel} Tournament</div>
          <h1 className="trp-title">{tournament.name}</h1>
          {tournament.startDate && (
            <p className="trp-date">
              {formatDate(tournament.startDate)}
              {tournament.endDate && tournament.endDate !== tournament.startDate && (
                <> – {formatDate(tournament.endDate)}</>
              )}
            </p>
          )}
          {tournament.venue && (
            <p className="trp-venue">{tournament.venue.name}{tournament.venue.address && ` · ${tournament.venue.address}`}</p>
          )}
          {tournament.description && (
            <p className="trp-description">{tournament.description}</p>
          )}
          <div className="trp-meta">
            {entryFee && <span className="trp-badge trp-badge-fee">{entryFee} entry</span>}
            {spotsRemaining != null && spotsRemaining <= 5 && spotsRemaining > 0 && (
              <span className="trp-badge trp-badge-warning">{spotsRemaining} spots left</span>
            )}
            {teamCount != null && tournament.maxTeams && (
              <span className="trp-badge">{teamCount} / {tournament.maxTeams} teams</span>
            )}
          </div>
        </div>

        {/* Registration blocked states */}
        {!registrationOpen && (
          <div className="trp-closed">
            {isFull
              ? 'This tournament is full.'
              : deadlinePassed
              ? 'The registration deadline has passed.'
              : tournament.status === 'draft'
              ? 'Registration is not open yet.'
              : 'Registration is closed.'}
          </div>
        )}

        {/* Registration Form */}
        {registrationOpen && (
          <form className="trp-form" onSubmit={handleSubmit} noValidate>
            <h2 className="trp-form-title">Register Your Team</h2>

            {submitError && <div className="trp-error-banner">{submitError}</div>}

            <div className="trp-field">
              <label className="trp-label">Your name <span className="trp-required">*</span></label>
              <input
                className="trp-input"
                type="text"
                name="playerName"
                value={form.playerName}
                onChange={handleChange}
                placeholder="First and last name"
                required
                autoComplete="name"
              />
            </div>

            <div className="trp-field">
              <label className="trp-label">Email</label>
              <input
                className="trp-input"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                autoComplete="email"
              />
              <span className="trp-hint">For registration confirmation</span>
            </div>

            <div className="trp-field">
              <label className="trp-label">Phone</label>
              <input
                className="trp-input"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="(optional)"
                autoComplete="tel"
              />
            </div>

            <div className="trp-field">
              <label className="trp-checkbox-label">
                <input
                  type="checkbox"
                  name="isFreeAgent"
                  checked={form.isFreeAgent}
                  onChange={handleChange}
                />
                <span>I'm a free agent (no team yet)</span>
              </label>
            </div>

            {!form.isFreeAgent && (
              <div className="trp-field">
                <label className="trp-label">Team name <span className="trp-required">*</span></label>
                <input
                  className="trp-input"
                  type="text"
                  name="teamName"
                  value={form.teamName}
                  onChange={handleChange}
                  placeholder="Your team's name"
                  required={!form.isFreeAgent}
                />
              </div>
            )}

            {showSkillLevel && (
              <div className="trp-field">
                <label className="trp-label">Skill level</label>
                <select
                  className="trp-input"
                  name="skillLevel"
                  value={form.skillLevel}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {SKILL_LEVELS.map(lvl => (
                    <option key={lvl} value={lvl}>{lvl}</option>
                  ))}
                </select>
              </div>
            )}

            {showWaiver && (
              <div className="trp-field">
                <div className="trp-waiver">
                  <p>By registering, you agree to participate in this tournament at your own risk and release the organizer from liability for injuries or damages.</p>
                </div>
                <label className="trp-checkbox-label">
                  <input
                    type="checkbox"
                    name="waiverAccepted"
                    checked={form.waiverAccepted}
                    onChange={handleChange}
                    required
                  />
                  <span>I have read and agree to the waiver <span className="trp-required">*</span></span>
                </label>
              </div>
            )}

            <button
              type="submit"
              className="trp-submit"
              disabled={submitting}
            >
              {submitting ? 'Registering...' : 'Register'}
            </button>
          </form>
        )}

        <div className="trp-footer">Powered by LeagueMaker</div>
      </div>
    </div>
  );
}

export default TournamentRegistrationPage;
